/* @refresh skip */
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { AdTranslationsForm } from '@/components/admin/AdTranslationsForm';
import { Plus, Edit, Trash2, Eye, EyeOff, Upload, X, ExternalLink, Home, Sparkles, BarChart3 } from 'lucide-react';
import { format } from 'date-fns';
import { AdvertisementFilters } from './AdvertisementFilters';
import { AdPreview } from './AdPreview';
import { AdvertisementAnalytics } from './AdvertisementAnalytics';

interface Advertisement {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  image_urls?: Record<string, string>;
  link_url: string | null;
  position: string;
  active: boolean;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  updated_at: string;
}

export const AdvertisementsManager = () => {
  const { toast } = useToast();
  const [ads, setAds] = useState<Advertisement[]>([]);
  const [filteredAds, setFilteredAds] = useState<Advertisement[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [selectedAdForAnalytics, setSelectedAdForAnalytics] = useState<string | null>(null);
  const [editingAd, setEditingAd] = useState<Advertisement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [linkType, setLinkType] = useState<'external' | 'local'>('external');
  const [imageStyle, setImageStyle] = useState('moderne');
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [positionFilter, setPositionFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    title_translations: {} as Record<string, string>,
    description_translations: {} as Record<string, string>,
    image_url: '',
    image_urls: {} as Record<string, string>,
    link_url: '',
    position: 'top',
    active: false,
    start_date: '',
    end_date: '',
    promotion_type: 'none',
    category_filter: '',
    discount_percentage: 0,
    auto_apply_discount: false
  });

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      const { data, error } = await supabase
        .from('advertisements')
        .select('*')
        .order('created_at', { ascending: false});

      if (error) throw error;
      setAds(data || []);
    } catch (error) {
      console.error('Erreur chargement publicités:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les publicités",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "Erreur",
          description: "L'image ne doit pas dépasser 5 Mo",
          variant: "destructive"
        });
        return;
      }

      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const generateImageWithAI = async (language: 'fr' | 'en' = 'fr') => {
    const title = language === 'en' 
      ? (formData.title_translations['en'] || formData.title)
      : (formData.title_translations['fr'] || formData.title);
    const description = language === 'en'
      ? (formData.description_translations['en'] || formData.description)
      : (formData.description_translations['fr'] || formData.description);

    if (!description && !title) {
      toast({
        title: "Erreur",
        description: `Veuillez entrer au moins un titre ou une description en ${language === 'en' ? 'anglais' : 'français'}`,
        variant: "destructive"
      });
      return;
    }

    setGeneratingImage(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-ad-image', {
        body: {
          description: description,
          title: title,
          style: imageStyle,
          language: language
        }
      });

      if (error) {
        console.error('Erreur edge function:', error);
        throw new Error(error.message || "Erreur lors de l'appel de la fonction");
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      if (data?.imageUrl) {
        // Convertir le base64 en blob et l'uploader
        const response = await fetch(data.imageUrl);
        const blob = await response.blob();
        const file = new File([blob], `ai-generated-${Date.now()}.png`, { type: 'image/png' });
        
        // Upload vers Supabase Storage
        const fileName = `ai_${Math.random().toString(36).substring(2)}_${Date.now()}.png`;
        const { error: uploadError } = await supabase.storage
          .from('advertisements')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('advertisements')
          .getPublicUrl(fileName);

        // Mettre à jour image_urls avec la nouvelle URL pour cette langue
        const updatedImageUrls = { ...formData.image_urls, [language]: publicUrl };
        setImagePreview(publicUrl);
        setFormData({ ...formData, image_url: publicUrl, image_urls: updatedImageUrls });
        
        toast({
          title: "Succès",
          description: `Image générée avec succès par l'IA en ${language === 'fr' ? 'français' : 'anglais'}`
        });
      } else {
        throw new Error("Aucune URL d'image retournée");
      }
    } catch (error) {
      console.error('Erreur génération image IA:', error);
      const errorMessage = error instanceof Error ? error.message : "Impossible de générer l'image avec l'IA";
      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setGeneratingImage(false);
    }
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile) return formData.image_url || null;

    setUploading(true);
    try {
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('advertisements')
        .upload(filePath, imageFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('advertisements')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Erreur upload image:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'uploader l'image",
        variant: "destructive"
      });
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const imageUrl = await uploadImage();
      
      if (imageFile && !imageUrl) {
        return;
      }

      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("Utilisateur non authentifié");
      }

      const adData = {
        title: formData.title,
        description: formData.description || null,
        title_translations: formData.title_translations,
        description_translations: formData.description_translations,
        image_url: imageUrl || null,
        image_urls: formData.image_urls || {},
        link_url: formData.link_url || null,
        position: formData.position,
        active: formData.active,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        created_by: user.id
      };

      if (editingAd) {
        const { error } = await supabase
          .from('advertisements')
          .update(adData)
          .eq('id', editingAd.id);

        if (error) throw error;

        toast({
          title: "Succès",
          description: "Publicité mise à jour avec succès"
        });
      } else {
        const { error } = await supabase
          .from('advertisements')
          .insert([adData]);

        if (error) throw error;

        toast({
          title: "Succès",
          description: "Publicité créée avec succès"
        });
      }

      setDialogOpen(false);
      resetForm();
      fetchAds();
    } catch (error: any) {
      console.error('Erreur sauvegarde publicité:', error);
      const errorMessage = error?.message || error?.details || "Impossible de sauvegarder la publicité";
      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  const handleEdit = (ad: Advertisement) => {
    setEditingAd(ad);
    setFormData({
      title: ad.title,
      description: ad.description || '',
      title_translations: (ad as any).title_translations || {},
      description_translations: (ad as any).description_translations || {},
      image_url: ad.image_url || '',
      image_urls: (ad as any).image_urls || {},
      link_url: ad.link_url || '',
      position: ad.position,
      active: ad.active,
      start_date: ad.start_date ? format(new Date(ad.start_date), 'yyyy-MM-dd\'T\'HH:mm') : '',
      end_date: ad.end_date ? format(new Date(ad.end_date), 'yyyy-MM-dd\'T\'HH:mm') : '',
      promotion_type: (ad as any).promotion_type || 'none',
      category_filter: (ad as any).category_filter || '',
      discount_percentage: (ad as any).discount_percentage || 0,
      auto_apply_discount: (ad as any).auto_apply_discount || false
    });
    
    if (ad.link_url) {
      setLinkType(ad.link_url.startsWith('http') ? 'external' : 'local');
    }
    
    if (ad.image_url) {
      setImagePreview(ad.image_url);
    }
    
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette publicité ?')) return;

    try {
      const { error } = await supabase
        .from('advertisements')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Succès",
        description: "Publicité supprimée avec succès"
      });

      fetchAds();
    } catch (error) {
      console.error('Erreur suppression publicité:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer la publicité",
        variant: "destructive"
      });
    }
  };

  const toggleActive = async (ad: Advertisement) => {
    try {
      const { error } = await supabase
        .from('advertisements')
        .update({ active: !ad.active })
        .eq('id', ad.id);

      if (error) throw error;

      toast({
        title: "Succès",
        description: `Publicité ${!ad.active ? 'activée' : 'désactivée'}`
      });

      fetchAds();
    } catch (error) {
      console.error('Erreur changement statut:', error);
      toast({
        title: "Erreur",
        description: "Impossible de modifier le statut",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      title_translations: {},
      description_translations: {},
      image_url: '',
      image_urls: {},
      link_url: '',
      position: 'top',
      active: false,
      start_date: '',
      end_date: '',
      promotion_type: 'none',
      category_filter: '',
      discount_percentage: 0,
      auto_apply_discount: false
    });
    setEditingAd(null);
    setImageFile(null);
    setImagePreview('');
    setLinkType('external');
    setImageStyle('moderne');
  };

  const isAdActive = (ad: Advertisement) => {
    if (!ad.active) return false;
    
    const now = new Date();
    const startDate = ad.start_date ? new Date(ad.start_date) : null;
    const endDate = ad.end_date ? new Date(ad.end_date) : null;

    if (startDate && now < startDate) return false;
    if (endDate && now > endDate) return false;

    return true;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Gestion des publicités</h2>
          <p className="text-muted-foreground">
            Gérez les espaces publicitaires sur la plateforme
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle publicité
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingAd ? 'Modifier la publicité' : 'Nouvelle publicité'}
              </DialogTitle>
              <DialogDescription>
                Configurez l'emplacement et la durée d'affichage de la publicité
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Formulaire */}
              <form onSubmit={handleSubmit} className="space-y-4">
              <AdTranslationsForm
                titleTranslations={formData.title_translations}
                descriptionTranslations={formData.description_translations}
                adId={editingAd?.id}
                onTitleChange={(translations) => {
                  setFormData(prev => ({ 
                    ...prev, 
                    title_translations: translations,
                    title: translations.fr || prev.title
                  }));
                }}
                onDescriptionChange={(translations) => {
                  setFormData(prev => ({ 
                    ...prev, 
                    description_translations: translations,
                    description: translations.fr || prev.description
                  }));
                }}
              />

              <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <Label className="text-base font-semibold">Génération d'image par IA</Label>
                </div>
                <div>
                  <Label htmlFor="imageStyle" className="text-sm">Style de l'image</Label>
                  <Select value={imageStyle} onValueChange={setImageStyle}>
                    <SelectTrigger id="imageStyle">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="moderne">Moderne et dynamique</SelectItem>
                      <SelectItem value="minimaliste">Minimaliste et épuré</SelectItem>
                      <SelectItem value="colore">Coloré et vibrant</SelectItem>
                      <SelectItem value="professionnel">Professionnel et corporate</SelectItem>
                      <SelectItem value="luxe">Luxueux et élégant</SelectItem>
                      <SelectItem value="jeune">Jeune et tendance</SelectItem>
                      <SelectItem value="naturel">Naturel et authentique</SelectItem>
                      <SelectItem value="technologique">Technologique et futuriste</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="default"
                    onClick={() => generateImageWithAI('fr')}
                    disabled={generatingImage || (!formData.title_translations['fr'] && !formData.description_translations['fr'])}
                    className="w-full"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    {generatingImage ? 'Génération...' : 'Générer en Français 🇫🇷'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => generateImageWithAI('en')}
                    disabled={generatingImage || (!formData.title_translations['en'] && !formData.description_translations['en'])}
                    className="w-full"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    {generatingImage ? 'Génération...' : 'Générer en English 🇬🇧'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  L'IA créera une image dans la langue sélectionnée basée sur votre titre, description et le style choisi
                </p>
              </div>

              <div className="space-y-3">
                <Label>Image de la publicité</Label>
                
                {imagePreview && (
                  <div className="relative w-full h-48 border rounded-lg overflow-hidden bg-muted">
                    <img 
                      src={imagePreview} 
                      alt="Aperçu" 
                      className="w-full h-full object-contain"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview('');
                        setFormData({ ...formData, image_url: '' });
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      id="image-file"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="cursor-pointer"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Formats acceptés: JPG, PNG, WEBP, GIF (max 5MB)
                    </p>
                  </div>
                </div>
                
                {!imageFile && (
                  <div className="text-sm text-muted-foreground">
                    ou utilisez une URL externe :
                    <Input
                      type="url"
                      value={formData.image_url}
                      onChange={(e) => {
                        setFormData({ ...formData, image_url: e.target.value });
                        setImagePreview(e.target.value);
                      }}
                      placeholder="https://..."
                      className="mt-2"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <Label htmlFor="link_url">Lien de destination</Label>
                <div className="flex gap-2 mb-2">
                  <Button
                    type="button"
                    variant={linkType === 'local' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setLinkType('local');
                      setFormData({ ...formData, link_url: '/' });
                    }}
                  >
                    <Home className="h-4 w-4 mr-2" />
                    Lien local
                  </Button>
                  <Button
                    type="button"
                    variant={linkType === 'external' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setLinkType('external');
                      setFormData({ ...formData, link_url: 'https://' });
                    }}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Lien externe
                  </Button>
                </div>
                
                {linkType === 'local' ? (
                  <Select 
                    value={formData.link_url} 
                    onValueChange={(value) => setFormData({ ...formData, link_url: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une page" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="/">Page d'accueil</SelectItem>
                      <SelectItem value="/products">Tous les produits</SelectItem>
                      <SelectItem value="/deals">Promotions</SelectItem>
                      <SelectItem value="/relay-points">Points relais</SelectItem>
                      <SelectItem value="/become-vendor">Devenir vendeur</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="link_url"
                    type="url"
                    value={formData.link_url}
                    onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                    placeholder="https://..."
                  />
                )}
                <p className="text-xs text-muted-foreground">
                  {linkType === 'local' 
                    ? 'Sélectionnez une page interne de votre site'
                    : 'Entrez une URL externe complète (https://...)'}
                </p>
              </div>

              <div>
                <Label htmlFor="position">Position</Label>
                <Select value={formData.position} onValueChange={(value) => setFormData({ ...formData, position: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="top">Haut de page</SelectItem>
                    <SelectItem value="hero">Section Hero</SelectItem>
                    <SelectItem value="middle">Milieu de page</SelectItem>
                    <SelectItem value="bottom">Bas de page</SelectItem>
                    <SelectItem value="sidebar">Barre latérale</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">Date de début</Label>
                  <Input
                    id="start_date"
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="end_date">Date de fin</Label>
                  <Input
                    id="end_date"
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="active"
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                />
                <Label htmlFor="active">Activer immédiatement</Label>
              </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" disabled={uploading}>
                    {uploading ? 'Upload en cours...' : editingAd ? 'Mettre à jour' : 'Créer'}
                  </Button>
                </DialogFooter>
              </form>

              {/* Prévisualisation */}
              <div className="lg:sticky lg:top-4 lg:self-start">
                <AdPreview
                  title={formData.title}
                  description={formData.description}
                  imageUrl={imagePreview || formData.image_url}
                  position={formData.position}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Publicités ({ads.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p>Chargement...</p>
          ) : ads.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              Aucune publicité configurée
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titre</TableHead>
                  <TableHead>Position</TableHead>
                  <TableHead>Période</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ads.map((ad) => (
                  <TableRow key={ad.id}>
                    <TableCell className="font-medium">{ad.title}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {ad.position === 'top' && 'Haut'}
                        {ad.position === 'middle' && 'Milieu'}
                        {ad.position === 'bottom' && 'Bas'}
                        {ad.position === 'sidebar' && 'Sidebar'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {ad.start_date && (
                        <div>Début: {format(new Date(ad.start_date), 'dd/MM/yyyy HH:mm')}</div>
                      )}
                      {ad.end_date && (
                        <div>Fin: {format(new Date(ad.end_date), 'dd/MM/yyyy HH:mm')}</div>
                      )}
                      {!ad.start_date && !ad.end_date && <span className="text-muted-foreground">Illimité</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {ad.active ? (
                          <>
                            {isAdActive(ad) ? (
                              <Badge className="bg-green-500">Active</Badge>
                            ) : (
                              <>
                                {ad.start_date && new Date() < new Date(ad.start_date) ? (
                                  <Badge className="bg-blue-500">Programmée</Badge>
                                ) : (
                                  <Badge variant="secondary">Expirée</Badge>
                                )}
                              </>
                            )}
                          </>
                        ) : (
                          <Badge variant="secondary">Désactivée</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSelectedAdForAnalytics(ad.id);
                          setAnalyticsDialogOpen(true);
                        }}
                        title="Voir les statistiques"
                      >
                        <BarChart3 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleActive(ad)}
                      >
                        {ad.active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(ad)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(ad.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Analytics Dialog */}
      <Dialog open={analyticsDialogOpen} onOpenChange={setAnalyticsDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Statistiques de la publicité</DialogTitle>
            <DialogDescription>
              Suivez les performances de votre publicité avec des métriques détaillées
            </DialogDescription>
          </DialogHeader>
          {selectedAdForAnalytics && (
            <AdvertisementAnalytics advertisementId={selectedAdForAnalytics} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
